import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Search, Package, MapPin, Loader2, ScanLine, Warehouse, X,
  ClipboardCheck, Send, CheckCircle2, XCircle, Clock, RefreshCw,
  History,
} from 'lucide-react';
import BarcodeScannerModal from './BarcodeScannerModal';
import { useToast } from '../hooks/use-toast';

/**
 * Feb 2026 — Floor Mode > Audit panel.
 *
 * A permitted sub-admin (`allow_floor_audit`) can scan or type a
 * Pallet ID / SKU / Location to find any pallet in-warehouse, then
 * submit a Request Move for a super admin to review. On approval
 * the pallet physically relocates (ledger + bin occupancy + stock
 * levels update together via the shared transfer helper), and the
 * requester gets a bell + web-push notification.
 *
 * Reuses the existing `GET /admin/floor/stock-check` endpoint for
 * search (that endpoint also honours `allow_floor_audit`), then
 * posts to `/admin/floor/audit/requests` on submit.
 */

const DEBOUNCE_MS = 250;

const StatusBadge = ({ status }) => {
  const map = {
    pending: { cls: 'bg-amber-100 text-amber-900', Icon: Clock },
    approved: { cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    rejected: { cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
  };
  const { cls, Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      <Icon className="w-3 h-3" /> {status || 'pending'}
    </span>
  );
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${hh}:${mm}`;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hh}:${mm}`;
};


const RequestMoveModal = ({ row, warehouses = [], API, authHeaders, onClose, onSubmitted }) => {
  const { toast } = useToast();
  const [toWh, setToWh] = useState(row.warehouse_id || warehouses[0]?.id || '');
  const [toZone, setToZone] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const canSubmit = toWh && toLoc.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/floor/audit/requests`,
        {
          placement_id: row.placement_id,
          to_warehouse_id: toWh,
          to_zone: toZone.trim(),
          to_location_id: toLoc.trim(),
          note: note.trim(),
        },
        { headers: authHeaders },
      );
      toast({ title: 'Move request submitted', description: 'A super admin will review it.' });
      onSubmitted?.(data);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Submit failed';
      toast({ title: 'Could not submit request', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        data-testid="audit-request-move-modal"
      >
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Request move</div>
            <div className="text-[15px] font-bold text-[#0F172A] leading-tight line-clamp-1">
              {row.product?.name || row.pallet_id || 'Pallet'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-xl hover:bg-slate-100 text-slate-500 flex items-center justify-center"
            data-testid="audit-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Current location snapshot */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12.5px] space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Currently here</div>
            <div className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-700">
              <ScanLine className="w-3.5 h-3.5" /> {row.pallet_id || '—'}
              <span className="text-slate-400">·</span>
              <MapPin className="w-3.5 h-3.5" /> {[row.zone, row.location_id].filter(Boolean).join(' · ') || '—'}
            </div>
            <div className="text-[11px] text-slate-500">
              Qty on pallet: <span className="font-bold tabular-nums">{row.on_hand ?? 0}</span> {row.product?.uom || 'ctn'}
            </div>
          </div>

          {/* Destination form */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Target warehouse</span>
              <select
                value={toWh}
                onChange={(e) => setToWh(e.target.value)}
                className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-slate-200 bg-white text-[14px] font-semibold focus:outline-none focus:border-amber-500"
                data-testid="audit-modal-warehouse"
              >
                <option value="">Select warehouse…</option>
                {(warehouses || []).map((w) => (
                  <option key={w.id} value={w.id}>{w.name || w.id}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Zone (optional)</span>
              <input
                type="text"
                value={toZone}
                onChange={(e) => setToZone(e.target.value)}
                placeholder="e.g. A"
                className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-slate-200 bg-white text-[14px] font-semibold focus:outline-none focus:border-amber-500"
                data-testid="audit-modal-zone"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Target location *</span>
              <div className="relative mt-1">
                <input
                  type="text"
                  value={toLoc}
                  onChange={(e) => setToLoc(e.target.value)}
                  placeholder="e.g. A-04-B2"
                  className="w-full h-11 pl-3 pr-16 rounded-xl border-2 border-slate-200 bg-white text-[14px] font-semibold focus:outline-none focus:border-amber-500"
                  data-testid="audit-modal-location"
                />
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-2 rounded-lg bg-slate-900 text-white text-[11px] font-bold inline-flex items-center gap-1"
                  data-testid="audit-modal-scan"
                >
                  <ScanLine className="w-3.5 h-3.5" /> Scan
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Reason / note (optional)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. Pallet is in a cold-storage bin but it's a dry SKU — needs to move to rack 4."
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-slate-200 bg-white text-[13px] focus:outline-none focus:border-amber-500 resize-none"
                data-testid="audit-modal-note"
              />
            </label>
          </div>
        </div>
        <div className="p-3 border-t border-slate-100 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[13px]"
            data-testid="audit-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-[1.4] h-11 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 disabled:text-slate-500 text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5"
            data-testid="audit-modal-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
      <BarcodeScannerModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onResult={(v) => { setScanOpen(false); setToLoc(String(v || '').trim()); }}
      />
    </div>
  );
};


const ResultCard = ({ row, onRequestMove }) => (
  <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`audit-card-${row.placement_id}`}>
    <div className="p-3.5 flex items-start gap-3">
      <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
        {row.product?.image_url
          ? <img src={row.product.image_url} alt="" className="w-full h-full object-cover" />
          : <Package className="w-6 h-6 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {row.product?.sku || '—'}
        </div>
        <div className="text-[15px] font-bold text-[#0F172A] leading-tight line-clamp-2">
          {row.product?.name || 'Unknown product'}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">On hand</div>
        <div className="text-2xl font-extrabold tabular-nums text-emerald-700">{row.on_hand}</div>
        <div className="text-[10px] font-semibold text-slate-500 -mt-0.5">{row.product?.uom || 'ctn'}</div>
      </div>
    </div>
    <div className="px-3.5 pb-2 flex flex-wrap items-center gap-1.5 text-[12px] font-semibold">
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2.5 py-1">
        <ScanLine className="w-3.5 h-3.5" /> {row.pallet_id || '—'}
      </span>
      {(row.zone || row.location_id) && (
        <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 px-2.5 py-1">
          <MapPin className="w-3.5 h-3.5" />
          {[row.zone, row.location_id].filter(Boolean).join(' · ')}
        </span>
      )}
      {row.warehouse_name && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-1">
          <Warehouse className="w-3.5 h-3.5" /> {row.warehouse_name}
        </span>
      )}
    </div>
    <button
      type="button"
      onClick={() => onRequestMove(row)}
      className="w-full py-2.5 border-t border-slate-100 flex items-center justify-center gap-1.5 text-[13px] font-bold text-amber-700 hover:bg-amber-50 active:bg-amber-100"
      data-testid={`audit-card-request-${row.placement_id}`}
    >
      <ClipboardCheck className="w-4 h-4" /> Request move
    </button>
  </div>
);


const MyRequestsStrip = ({ API, authHeaders, refreshKey }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/floor/audit/requests/mine?limit=10`,
        { headers: authHeaders },
      );
      setRows(Array.isArray(data?.results) ? data.results : []);
    } catch {
      // ignore — strip is a nice-to-have
    } finally {
      setBusy(false);
    }
  };
  // Load on mount, on refresh key change, and every 20s.
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const pendingCount = rows.filter((r) => r.status === 'pending').length;
  const last = rows[0];

  if (!rows.length && !busy) return null;

  return (
    <div className="mb-3" data-testid="audit-my-requests">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 text-[12px] font-semibold text-slate-700"
      >
        <span className="inline-flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" />
          My requests ({rows.length})
          {pendingCount > 0 && (
            <span className="ml-1 inline-flex items-center gap-1 bg-amber-100 text-amber-900 rounded-full px-2 py-0.5 text-[10px] font-bold">
              <Clock className="w-3 h-3" /> {pendingCount} pending
            </span>
          )}
        </span>
        <RefreshCw
          className={`w-3.5 h-3.5 text-slate-400 ${busy ? 'animate-spin' : ''}`}
          onClick={(e) => { e.stopPropagation(); load(); }}
          data-testid="audit-my-requests-refresh"
        />
      </button>
      {!open && last && (
        <div className="mt-1 px-3 py-1.5 rounded-xl bg-white border border-slate-100 flex items-center gap-2 text-[11.5px]" data-testid="audit-my-requests-last">
          <StatusBadge status={last.status} />
          <span className="font-semibold text-slate-700 truncate flex-1">
            {last.pallet_id || last.product_name || 'Pallet'} → {last.to_location_id}
          </span>
          <span className="text-slate-400">{fmtTs(last.resolved_at || last.requested_at)}</span>
        </div>
      )}
      {open && (
        <div className="mt-1 space-y-1" data-testid="audit-my-requests-list">
          {rows.map((r) => (
            <div
              key={r.id}
              className="px-3 py-2 rounded-xl bg-white border border-slate-100 text-[12px]"
              data-testid={`audit-my-request-${r.id}`}
            >
              <div className="flex items-center gap-2">
                <StatusBadge status={r.status} />
                <span className="font-bold text-slate-700 truncate flex-1">
                  {r.pallet_id || r.product_name || 'Pallet'}
                </span>
                <span className="text-slate-400">{fmtTs(r.resolved_at || r.requested_at)}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500 pl-1">
                {r.from_location_id || '—'} → {r.to_location_id}
                {r.decision_note && (
                  <span className="ml-2 text-rose-700 font-semibold">· {r.decision_note}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/**
 * Props:
 *   - API, authHeaders — from AdminAuthContext.
 *   - warehouseId — the Floor-Mode top filter.
 *   - warehouses — list of accessible warehouses (from `/admin/warehouses/me`).
 */
const FloorAuditPanel = ({ API, authHeaders, warehouseId, warehouses = [] }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [requestFor, setRequestFor] = useState(null); // row currently being requested
  const [refreshKey, setRefreshKey] = useState(0);
  const inputRef = useRef(null);

  // Reuse the Stock Check endpoint — it already honours the
  // `allow_floor_audit` toggle via `_resolve_stock_check_access`.
  useEffect(() => {
    const t = (q || '').trim();
    if (!t) { setResults([]); setErr(''); setTruncated(false); return undefined; }
    const handle = setTimeout(async () => {
      setBusy(true); setErr('');
      try {
        const { data } = await axios.get(`${API}/admin/floor/stock-check`, {
          headers: authHeaders,
          params: { q: t, group_by: 'placement', include_zero: false, limit: 50 },
        });
        const rows = Array.isArray(data?.results) ? data.results : [];
        setResults(rows);
        setTruncated(!!data?.truncated);
      } catch (e) {
        setErr(e?.response?.data?.detail || e?.message || 'Search failed');
        setResults([]); setTruncated(false);
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q, API, authHeaders]);

  const filtered = useMemo(() => {
    if (!warehouseId) return results;
    return results.filter((r) => !r.warehouse_id || r.warehouse_id === warehouseId);
  }, [results, warehouseId]);

  const empty = !busy && !err && q.trim() && filtered.length === 0;

  return (
    <div className="px-3 sm:px-4 pb-24 pt-3" data-testid="audit-panel">
      {/* Sticky search bar */}
      <div className="sticky top-0 z-20 -mx-3 sm:-mx-4 px-3 sm:px-4 pb-3 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <div className="relative">
          <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            placeholder="Scan or type Pallet, SKU, or Location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-14 pl-10 pr-24 rounded-2xl border-2 border-slate-200 bg-white text-[16px] font-semibold focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 transition"
            data-testid="audit-search-input"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {q && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => { setQ(''); inputRef.current?.focus(); }}
                className="w-9 h-9 rounded-xl hover:bg-slate-100 text-slate-500 flex items-center justify-center"
                data-testid="audit-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              className="h-10 px-3 rounded-xl bg-slate-900 text-white text-[13px] font-bold inline-flex items-center gap-1.5"
              data-testid="audit-search-scan"
            >
              <ScanLine className="w-4 h-4" /> Scan
            </button>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] font-semibold text-slate-500">
          <span>
            {busy ? (
              <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</span>
            ) : q.trim() ? (
              <span data-testid="audit-count">{filtered.length} result{filtered.length === 1 ? '' : 's'}{truncated ? ' · showing top 50' : ''}</span>
            ) : (
              <span>Scan any pallet to flag it for a move · Super admin reviews each request</span>
            )}
          </span>
        </div>
      </div>

      <MyRequestsStrip API={API} authHeaders={authHeaders} refreshKey={refreshKey} />

      {!q.trim() && (
        <div className="mt-6 text-center text-slate-400" data-testid="audit-hint">
          <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <div className="text-[13px] font-semibold text-slate-500">Audit any pallet, location, or SKU</div>
          <div className="text-[11px] mt-1 max-w-xs mx-auto">
            Flag a wrong placement — the super admin will approve the move.
          </div>
        </div>
      )}

      {err && (
        <div className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700" data-testid="audit-error">
          {err}
        </div>
      )}

      {empty && (
        <div className="mt-6 text-center text-slate-500" data-testid="audit-empty">
          <div className="text-[13px] font-semibold">No stock found for "{q.trim()}"</div>
          <div className="text-[11px] mt-1 text-slate-400">Try a partial SKU or a location code.</div>
        </div>
      )}

      <div className="mt-3 space-y-3">
        {filtered.map((r) => (
          <ResultCard key={r.placement_id} row={r} onRequestMove={setRequestFor} />
        ))}
      </div>

      {requestFor && (
        <RequestMoveModal
          row={requestFor}
          warehouses={warehouses}
          API={API}
          authHeaders={authHeaders}
          onClose={() => setRequestFor(null)}
          onSubmitted={() => setRefreshKey((k) => k + 1)}
        />
      )}

      <BarcodeScannerModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onResult={(v) => { setScanOpen(false); setQ(String(v || '').trim()); }}
      />
    </div>
  );
};

export default FloorAuditPanel;
